import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import * as d3 from "d3";

// STYLES
import "./WeeklyBPBarChart.scss";
import { useDragToScroll } from "../../../hooks/useDragToScroll";

export type BPCheckInAverage = {
    systolicValue: number;
    diastolicValue: number;
    heartRateValue: number;
    weekStart: string;
    weekEnd: string;
};

export enum BPCheckinBarGraphType {
    systolic = "systolicValue",
    diastolic = "diastolicValue",
    heartRate = "heartRateValue",
}

interface IBarChartGraphProps {
    data: BPCheckInAverage[];
    barColor: string;
    graphType: BPCheckinBarGraphType;
    inPrintMode: boolean;
    width?: number;
    className?: string;
}

const BarChartGraph: React.FC<IBarChartGraphProps> = ({
    data,
    barColor,
    graphType,
    inPrintMode,
    width = 300,
    className = "",
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    //const containerRef = useRef<HTMLDivElement>(null);

    const { ref: scrollRef, isDragging } = useDragToScroll();

    // TODO - remove
    //const [fitsContainer, setFitsContainer] = useState(false);

    const [hasOverflow, setHasOverflow] = useState(false);
    const [atEnd, setAtEnd] = useState(false);
    const [atStart, setAtStart] = useState(true);

    useLayoutEffect(() => {
        const c = scrollRef.current;
        if (!c) return;
        setHasOverflow(c.scrollWidth > c.clientWidth + 1);
        setAtEnd(c.scrollLeft + c.clientWidth >= c.scrollWidth - 1);
        setAtStart(c.scrollLeft <= 1); // At start if scrollLeft is 0 (or very close to 0)
    }, [data, width, inPrintMode, scrollRef]);

    useEffect(() => {
        const c = scrollRef.current;
        if (!c || !hasOverflow) return;

        const onScroll = () => {
            setAtEnd(c.scrollLeft + c.clientWidth >= c.scrollWidth - 1);
            setAtStart(c.scrollLeft <= 1);
        };
        c.addEventListener("scroll", onScroll, { passive: true });
        return () => c.removeEventListener("scroll", onScroll);
    }, [hasOverflow, scrollRef]);

    useEffect(() => {
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const effectiveWidth = inPrintMode ? 600 : width;
        const height = 300;
        const margin = { top: 20, right: 10, bottom: 40, left: 10 };
        const innerHeight = height - margin.top - margin.bottom;

        const minBarWidth = 35;
        const maxBarWidth = 42;
        const minGap = 5;
        const maxGap = 20;
        const barCount = data.length;

        const idealSpacePerBar = effectiveWidth / barCount;
        let barWidth = Math.min(
            Math.max(idealSpacePerBar * 0.6, minBarWidth),
            maxBarWidth,
        );
        let gap = Math.min(Math.max(idealSpacePerBar * 0.4, minGap), maxGap);

        const chartWidth = barCount * (barWidth + gap);

        const yMax = d3.max(data, (d) => d[graphType]) ?? 100;
        const yScale = d3
            .scaleLinear()
            .domain([0, yMax])
            .range([innerHeight, 0]);

        const xScale = d3
            .scaleBand()
            .domain(data.map((d) => d.weekStart))
            .range([0, chartWidth])
            .paddingInner(gap / (barWidth + gap))
            .paddingOuter(0);

        svg.attr(
            "width",
            Math.max(chartWidth + margin.left + margin.right, width),
        ).attr("height", height);

        const g = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const radius = 6;

        g.selectAll("path.rounded-bar")
            .data(data)
            .enter()
            .append("path")
            .attr("class", "rounded-bar")
            .attr("d", (d) => {
                const x = xScale(d.weekStart)!;
                const y = yScale(d[graphType]);
                const w = xScale.bandwidth();
                const h = innerHeight - y;
                const r = Math.min(radius, h / 2, w / 2);
                const p = d3.path();
                p.moveTo(x + r, y);
                p.arcTo(x, y, x, y + r, r);
                p.lineTo(x, y + h - r);
                p.arcTo(x, y + h, x + r, y + h, r);
                p.lineTo(x + w - r, y + h);
                p.arcTo(x + w, y + h, x + w, y + h - r, r);
                p.lineTo(x + w, y + r);
                p.arcTo(x + w, y, x + w - r, y, r);
                p.closePath();
                return p.toString();
            })
            .attr("fill", barColor);

        g.selectAll("text.value")
            .data(data)
            .enter()
            .append("text")
            .attr("class", "value")
            .attr("x", (d) => xScale(d.weekStart)! + xScale.bandwidth() / 2)
            .attr("y", (d) => yScale(d[graphType]) - 5)
            .attr("text-anchor", "middle")
            .attr("fill", "black")
            .attr("font-size", "12px")
            .text((d) => Math.round(d[graphType]) || "");

        g.selectAll("text.label")
            .data(data)
            .enter()
            .append("text")
            .attr("class", "label")
            .attr("x", (d) => xScale(d.weekStart)! + xScale.bandwidth() / 2)
            .attr("y", innerHeight + 15)
            .attr("text-anchor", "middle")
            .attr("fill", "black")
            .attr("font-size", "11px")
            .text((d, idx) => {
                const start = new Date(`${d.weekStart}T00:00:00Z`);
                const end = new Date(`${d.weekEnd}T00:00:00Z`);
                const startDay = start.getUTCDate();
                const endDay = end.getUTCDate();
                const startMonth = start.toLocaleString("default", {
                    month: "short",
                    timeZone: "UTC",
                });
                const endMonth = end.toLocaleString("default", {
                    month: "short",
                    timeZone: "UTC",
                });

                const range =
                    startDay === endDay
                        ? `${startDay}`
                        : `${startDay}–${endDay}`;
                const monthDisplay =
                    startMonth === endMonth
                        ? startMonth
                        : `${startMonth}-${endMonth}`;
                return `${range}\n${monthDisplay}`;
            })
            .call((text) =>
                text.each(function () {
                    const self = d3.select(this);
                    const lines = self.text().split("\n");
                    self.text(null);
                    lines.forEach((line, i) => {
                        self.append("tspan")
                            .attr("x", self.attr("x"))
                            .attr("dy", i === 0 ? "0em" : "1.2em")
                            .text(line);
                    });
                }),
            );

        //const container = scrollRef.current;
        //const svgEl = svgRef.current;
        // if (container && svgEl) {
        //     //const svgW = svgEl.getBoundingClientRect().width;
        //     //const contW = container.getBoundingClientRect().width;
        //     //setFitsContainer(svgW <= contW + 0.5);
        // }
    }, [data, barColor, width, graphType, inPrintMode, scrollRef]);

    return (
        <div
            className={clsx("WeeklyBPBarChartWrapper", {
                "fade-active": hasOverflow && !atEnd,
                "fade-left-active": hasOverflow && !atStart,
            })}
        >
            <div
                ref={scrollRef}
                className={clsx("WeeklyBPBarChart", className, {
                    "is-dragging": isDragging,
                })}
            >
                <svg ref={svgRef} />
            </div>
        </div>
    );
};

export default BarChartGraph;
